import { slugify } from '../_default.js';
import { load as loadYaml, dump as dumpYaml, CORE_SCHEMA } from 'js-yaml';

const MAX_IMPORTED_SKILL_CONTENT_LENGTH = 10000;
const SKILL_MD_FILE_NAME = 'SKILL.md';
const SKILL_MD_FILE_NAME_UPPER = SKILL_MD_FILE_NAME.toUpperCase();
const TEXT_DECODER = new TextDecoder('utf-8');
const TEXT_FILE_EXTENSIONS = new Set([
  '.c',
  '.cc',
  '.cfg',
  '.conf',
  '.cpp',
  '.css',
  '.csv',
  '.env',
  '.gitignore',
  '.go',
  '.html',
  '.java',
  '.js',
  '.json',
  '.jsx',
  '.md',
  '.markdown',
  '.mjs',
  '.py',
  '.rb',
  '.rs',
  '.sh',
  '.sql',
  '.svg',
  '.toml',
  '.ts',
  '.tsx',
  '.txt',
  '.xml',
  '.yaml',
  '.yml',
]);

const SKILL_ARCHIVE_ERROR_TRANSLATION_KEYS = new Map([
  ['SKILL_ARCHIVE_INVALID', 'tone.skillImportInvalidArchive'],
  ['SKILL_MD_MISSING', 'tone.skillImportMissingInstructions'],
  ['SKILL_MD_UNREADABLE', 'tone.skillImportUnreadableInstructions'],
  ['SKILL_MD_EMPTY', 'tone.skillImportEmptyInstructions'],
  ['SKILL_METADATA_INVALID', 'tone.skillImportInvalidMetadata'],
  ['SKILL_NAME_INVALID', 'tone.skillImportInvalidName'],
  ['SKILL_NAME_MISSING', 'tone.skillImportMissingName'],
]);

const createSkillArchiveError = function(code, cause = null){
  const error = new Error(code);
  error.name = 'SkillArchiveError';
  error.code = code;

  if(cause){
    error.cause = cause;
  }

  return error;
};

const getSkillTextContent = function(skill = null){
  return String(skill?.content || skill?.briefing || '').trim();
};

const getFileExtension = function(path = ''){
  const normalizedPath = String(path || '').toLowerCase();
  const fileName = normalizedPath.split('/').pop() || '';
  const extensionMatch = fileName.match(/(\.[^.]+)$/);
  return extensionMatch ? extensionMatch[1] : '';
};

const getFrontMatterMatch = function(content = ''){
  const text = String(content || '').replace(/^\uFEFF/, '');
  if(!/^---[ \t]*\r?\n/.test(text)){
    return null;
  }

  return text.match(/^---[ \t]*\r?\n([\s\S]*?)^---[ \t]*(?:\r?\n|$)/m);
};

const stripFrontMatter = function(content = ''){
  const text = String(content || '').replace(/^\uFEFF/, '');
  const frontMatterMatch = getFrontMatterMatch(text);
  return (frontMatterMatch ? text.slice(frontMatterMatch[0].length) : text).trim();
};

const getSkillNameFromContent = function(content = ''){
  const frontMatterMatch = getFrontMatterMatch(content);
  if(!frontMatterMatch){
    if(/^\uFEFF?---[ \t]*(?:\r?\n|$)/.test(String(content || ''))){
      throw createSkillArchiveError('SKILL_METADATA_INVALID');
    }
    return '';
  }

  let metadata;
  try {
    metadata = loadYaml(frontMatterMatch[1], { schema: CORE_SCHEMA });
  } catch (error) {
    throw createSkillArchiveError('SKILL_METADATA_INVALID', error);
  }

  if(metadata == null){
    return '';
  }
  if(typeof metadata !== 'object' || Array.isArray(metadata)){
    throw createSkillArchiveError('SKILL_METADATA_INVALID');
  }

  const name = Object.hasOwn(metadata, 'name') ? metadata.name : null;
  if(name == null){
    return '';
  }
  if(typeof name !== 'string'){
    throw createSkillArchiveError('SKILL_NAME_INVALID');
  }
  return name.trim();
};

const shouldSkipArchiveEntry = function(path = ''){
  const normalizedPath = String(path || '');
  const fileName = normalizedPath.split('/').pop() || '';

  return (
    !normalizedPath ||
    normalizedPath.startsWith('__MACOSX/') ||
    fileName === '.DS_Store'
  );
};

const isProbablyText = function(uint8Array){
  if(!(uint8Array instanceof Uint8Array) || uint8Array.length === 0){
    return true;
  }

  let suspiciousByteCount = 0;
  const maxBytesToInspect = Math.min(uint8Array.length, 512);

  for(let i = 0; i < maxBytesToInspect; i += 1){
    const value = uint8Array[i];
    if(value === 0){
      return false;
    }

    const isAllowedControlCharacter = value === 9 || value === 10 || value === 13;
    const isPrintableAscii = value >= 32 && value <= 126;
    const isExtendedByte = value >= 128;

    if(!isAllowedControlCharacter && !isPrintableAscii && !isExtendedByte){
      suspiciousByteCount += 1;
    }
  }

  return suspiciousByteCount / maxBytesToInspect < 0.05;
};

const readArchiveTextEntry = async function(entry){
  const uint8Array = await entry.async('uint8array');
  if(!isProbablyText(uint8Array)){
    return null;
  }

  return TEXT_DECODER.decode(uint8Array);
};

const isTextArchiveEntry = function(path = '', textContent = ''){
  const extension = getFileExtension(path);
  if(TEXT_FILE_EXTENSIONS.has(extension)){
    return true;
  }

  const fileName = String(path || '').split('/').pop() || '';
  if(fileName.toUpperCase() === SKILL_MD_FILE_NAME){
    return true;
  }

  return /\S/.test(String(textContent || ''));
};

const getSkillArchiveErrorTranslationKey = function(error){
  const errorCode = String(error?.code || error?.message || '').trim();
  return SKILL_ARCHIVE_ERROR_TRANSLATION_KEYS.get(errorCode) || null;
};

const isInvalidSkillArchiveError = function(error){
  return getSkillArchiveErrorTranslationKey(error) !== null;
};

const extractSkillArchiveFromFile = async function(file){
  if(!globalThis.JSZip){
    throw new Error('JSZIP_UNAVAILABLE');
  }

  const arrayBuffer = await file.arrayBuffer();
  let zip = null;

  try {
    zip = await globalThis.JSZip.loadAsync(arrayBuffer);
  } catch (error) {
    throw createSkillArchiveError('SKILL_ARCHIVE_INVALID', error);
  }

  const archiveEntries = Object.values(zip.files)
    .filter((entry) => !entry.dir && !shouldSkipArchiveEntry(entry.name))
    .sort((a, b) => a.name.localeCompare(b.name));

  const skillEntry = archiveEntries.find((entry) => {
    const normalizedEntryName = String(entry.name || '').replace(/\\/g, '/');
    const entryFileName = normalizedEntryName.split('/').pop() || '';
    return entryFileName.toUpperCase() === SKILL_MD_FILE_NAME_UPPER;
  });
  if(!skillEntry){
    throw createSkillArchiveError('SKILL_MD_MISSING');
  }

  let skillMdContent;
  try {
    skillMdContent = await readArchiveTextEntry(skillEntry);
  } catch (error) {
    throw createSkillArchiveError('SKILL_MD_UNREADABLE', error);
  }
  if(typeof skillMdContent !== 'string'){
    throw createSkillArchiveError('SKILL_MD_UNREADABLE');
  }
  if(!skillMdContent.trim()){
    throw createSkillArchiveError('SKILL_MD_EMPTY');
  }

  const skillPathParts = String(skillEntry.name).replace(/\\/g, '/').split('/').filter(Boolean);
  const folderName = skillPathParts.length > 1 ? skillPathParts.at(-2).trim() : '';
  const name = getSkillNameFromContent(skillMdContent) || folderName;
  if(!name){
    throw createSkillArchiveError('SKILL_NAME_MISSING');
  }

  const additionalTextEntries = [];
  for(const entry of archiveEntries){
    if(entry.name === skillEntry.name){
      continue;
    }

    const textContent = await readArchiveTextEntry(entry);
    if(typeof textContent !== 'string' || !isTextArchiveEntry(entry.name, textContent)){
      continue;
    }

    additionalTextEntries.push({
      path: entry.name,
      content: textContent,
    });
  }

  const combinedContent = [
    skillMdContent.trim(),
    ...additionalTextEntries.map((entry) => `[File: ${entry.path}]\n${entry.content.trim()}`),
  ]
    .filter(Boolean)
    .join('\n\n')
    .slice(0, MAX_IMPORTED_SKILL_CONTENT_LENGTH);

  return {
    name,
    skillMdContent,
    content: combinedContent,
    additionalTextEntries,
  };
};

const createSkillArchiveBlob = async function(skill = null){
  if(!globalThis.JSZip){
    throw new Error('JSZIP_UNAVAILABLE');
  }

  const name = String(skill?.name || '').trim();
  const skillBody = stripFrontMatter(getSkillTextContent(skill));
  if(!name || !skillBody){
    throw new Error('SKILL_EXPORT_INVALID');
  }

  const skillMdContent = `---\n${dumpYaml({ name }, { schema: CORE_SCHEMA })}---\n\n${skillBody}\n`;
  const zip = new globalThis.JSZip();
  zip.file(SKILL_MD_FILE_NAME, skillMdContent);

  return {
    blob: await zip.generateAsync({ type: 'blob' }),
    fileName: `${slugify(name) || 'skill'}.skill`,
  };
};

export {
  MAX_IMPORTED_SKILL_CONTENT_LENGTH,
  createSkillArchiveBlob,
  extractSkillArchiveFromFile,
  getSkillNameFromContent,
  getSkillArchiveErrorTranslationKey,
  getSkillTextContent,
  isInvalidSkillArchiveError,
  stripFrontMatter,
};
